/**
 * AI Model Parameter Schema Definitions
 * @package @genieartinc/schemas
 *
 * Defines parameter schemas for AI models with Genie UI extensions.
 * Based on OpenAPI 3.x schema format with custom extensions for node system.
 */

import { z } from 'zod';

/**
 * Handle types for Genie node system
 */
export const HandleTypeSchema = z.enum([
  'text',      // Text input/output (prompts, captions)
  'image',     // Image file input/output
  'audio',     // Audio file input/output
  'video',     // Video file input/output
  'mask',      // Mask image (for inpainting)
  'file',      // Generic file input
  'number',    // Numeric value
  'boolean',   // Boolean value
  '3d',        // 3D model data
  'code',      // Code/script content
  'any',       // Any data type
]);

export type HandleType = z.infer<typeof HandleTypeSchema>;

/**
 * UI component types for settings panel
 */
export const UIComponentTypeSchema = z.enum([
  'slider',          // Numeric slider (for min/max ranges)
  'select',          // Dropdown select (for enum values)
  'input',           // Text input field
  'textarea',        // Multi-line text input
  'toggle',          // Boolean toggle switch
  'number',          // Numeric input (for integers without range)
  'file',            // File upload button
  'color',           // Color picker
  'select-or-input', // Combo select with freeform input
  'multi-select',    // Multi-value select
  'seed',            // Seed input (randomizable integer)
]);

export type UIComponentType = z.infer<typeof UIComponentTypeSchema>;

/**
 * Genie UI extensions for parameter definitions
 * Minimal extensions derived from OpenAPI fields only - NO hard-coded logic
 */
export const GenieUIExtensionSchema = z.object({
  // Node system integration (set when format="uri" or format="binary")
  isNodeInput: z.boolean().default(false),
  handleType: HandleTypeSchema.optional(),
  handlePosition: z.enum(['left', 'right', 'top', 'bottom']).optional().default('left'),

  // Settings panel component (derived from type/enum/min/max)
  component: UIComponentTypeSchema.optional(),
});

export type GenieUIExtension = z.infer<typeof GenieUIExtensionSchema>;

/**
 * OpenAPI parameter type
 */
export const ParameterTypeSchema = z.enum([
  'string',
  'integer',
  'number',
  'boolean',
  'array',
  'object',
]);

export type ParameterType = z.infer<typeof ParameterTypeSchema>;

/**
 * Parameter definition (OpenAPI + Genie extensions)
 */
export const ParameterDefinitionSchema = z.object({
  // ==================== OpenAPI 3.x Standard Fields ====================
  type: ParameterTypeSchema,
  title: z.string().optional(),
  description: z.string().optional(),
  default: z.unknown().optional(),

  // Numeric constraints
  minimum: z.number().optional(),
  maximum: z.number().optional(),
  exclusiveMinimum: z.boolean().optional(),
  exclusiveMaximum: z.boolean().optional(),
  multipleOf: z.number().optional(),

  // String constraints
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  pattern: z.string().optional(),
  format: z.string().optional(),

  // Enum values
  enum: z.array(z.unknown()).optional(),

  // Array constraints
  items: z.unknown().optional(),
  minItems: z.number().optional(),
  maxItems: z.number().optional(),
  uniqueItems: z.boolean().optional(),

  // Object constraints
  properties: z.record(z.string(), z.unknown()).optional(),
  required: z.array(z.string()).optional(),
  additionalProperties: z.unknown().optional(),

  // References
  $ref: z.string().optional(),

  // ==================== Genie Extensions ====================
  genieUI: GenieUIExtensionSchema.optional(),
});

export type ParameterDefinition = z.infer<typeof ParameterDefinitionSchema>;

/**
 * Complete parameter schema for an AI model
 */
export const AIModelParameterSchemaSchema = z.object({
  // Schema metadata
  version: z.string().default('v1'),
  fetchedAt: z.string().datetime().optional(),
  source: z.enum(['fal', 'openrouter', 'manual']).optional(),

  // Parameter definitions
  properties: z.record(z.string(), ParameterDefinitionSchema),
  required: z.array(z.string()).default([]),

  // OpenAPI definitions (for $ref resolution)
  definitions: z.record(z.string(), z.unknown()).optional(),

  // Genie metadata
  notes: z.string().optional(),
  verified: z.boolean().default(false),
});

export type AIModelParameterSchema = z.infer<typeof AIModelParameterSchemaSchema>;

/**
 * Schema fetch result (for sync system)
 */
export const SchemaFetchResultSchema = z.object({
  modelId: z.string(),
  success: z.boolean(),
  schema: AIModelParameterSchemaSchema.optional(),
  error: z.string().optional(),
  fetchedAt: z.string().datetime(),
});

export type SchemaFetchResult = z.infer<typeof SchemaFetchResultSchema>;

/**
 * Type guard: Check if parameter should be a node input
 */
export function isNodeInput(param: ParameterDefinition): boolean {
  return param.genieUI?.isNodeInput ?? false;
}

/**
 * Type guard: Check if parameter is a file input
 */
export function isFileInput(param: ParameterDefinition): boolean {
  const handleType = param.genieUI?.handleType;
  return handleType ? ['image', 'audio', 'video', 'mask', 'file'].includes(handleType) : false;
}

/**
 * Type guard: Check if parameter is required
 */
export function isRequired(paramName: string, schema: AIModelParameterSchema): boolean {
  return schema.required.includes(paramName);
}

/**
 * Helper: Get parameter display name
 */
export function getDisplayName(paramName: string, param: ParameterDefinition): string {
  return param.title || humanize(paramName);
}

/**
 * Helper: Humanize parameter name
 */
function humanize(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}
